import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sun, Heart, Flower2, Users, BookOpen, Sparkles,
  ChevronDown, ChevronUp,
} from "lucide-react";

const FONT_URL = 'https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Outfit:wght@300;400;500;600;700&family=Comic+Neue:wght@400;700&display=swap';

const css = `
  :root {
    --pink: #FF8FAB;
    --yellow: #FFEB3B;
    --purple: #C084FC;
    --sky: #7DD3FC;
    --gold: #FACC15;
    --mint: #6EE7B7;
  }

  .easter-root {
    font-family: 'Outfit', system-ui, sans-serif;
    background: linear-gradient(180deg, #FEFCE8 0%, #E0F2FE 100%);
    color: #1E2937;
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
  }

  .easter-root::before {
    content: '';
    position: fixed;
    inset: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(251,191,36,0.15) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(192,132,252,0.12) 0%, transparent 50%);
    pointer-events: none;
    z-index: 0;
  }

  .easter-inner {
    position: relative;
    z-index: 1;
    max-width: 620px;
    margin: 0 auto;
    padding: 1.5rem 1rem 6rem;
  }

  .serif { font-family: 'DM Serif Display', Georgia, serif; }
  .comic { font-family: 'Comic Neue', cursive; }
`;

const stages = [
  {
    id: "mary",
    num: "01",
    label: "Mary Goes to the Tomb",
    sub: "It was still dark outside...",
    Icon: Sun,
    accent: "#FACC15",
    image: "/images/marygoestotomb.jpeg",
    summary: `It was very early Sunday morning. The sky was turning pink and purple. Mary Magdalene loved Jesus with all her heart. She walked quietly to the big stone tomb where Jesus' body had been laid.

When she got there\u2026 the huge heavy stone was rolled away! The tomb was empty!

Mary felt so surprised and sad. "Where is Jesus?" she whispered. She ran to tell Peter and John the news!`,
  },
  {
    id: "peter",
    num: "02",
    label: "Peter and John Run!",
    sub: "Who will get there first?",
    Icon: Users,
    accent: "#7DD3FC",
    image: "/images/peterandjohnrun.jpeg",
    summary: `Peter and John heard the news and started running as fast as they could! John was faster and reached the tomb first. He peeked inside and saw the white cloths that had wrapped Jesus, but Jesus was gone!

Peter went right inside. The cloths were folded so neatly. Everything felt peaceful.

John looked and believed something wonderful had happened. They went home wondering\u2026 but Mary stayed at the garden crying.`,
  },
  {
    id: "jesus-mary",
    num: "03",
    label: "Jesus Calls Her Name",
    sub: '"Mary!"',
    Icon: Heart,
    accent: "#FF8FAB",
    image: "/images/jesusandmary.jpeg",
    summary: `Mary stayed by the tomb, crying softly. She looked inside and saw two bright shining angels!

Then she turned around and saw a kind man standing there. She thought He was the gardener.

"Woman, why are you crying?" He asked gently.

Mary said, "If you took Jesus, please tell me where He is!"

Then the man said her name so lovingly\u2026 "Mary!"

She looked up, her eyes sparkling with joy, and shouted, "Teacher! It's You! You're alive!"

Jesus smiled and said, "Go tell My friends I am going back to My Father in heaven."`,
  },
  {
    id: "disciples",
    num: "04",
    label: "Jesus Visits His Friends",
    sub: '"Peace be with you!"',
    Icon: Sparkles,
    accent: "#C084FC",
    image: "/images/jesusanddisciples.jpeg",
    summary: `That same evening the disciples were hiding in a locked room because they were scared. Suddenly \u2014 Jesus was standing right there with them!

"Peace be with you!" He said with a big warm smile.

He showed them the nail marks in His hands and side. The friends were so happy they laughed and cried and hugged each other!

Jesus breathed on them and said, "Receive the Holy Spirit." It was the happiest day ever!`,
  },
  {
    id: "thomas",
    num: "05",
    label: "Thomas Believes",
    sub: '"My Lord and my God!"',
    Icon: BookOpen,
    accent: "#6EE7B7",
    image: "/images/jesusandthomas.jpeg",
    summary: `Thomas wasn't there that night. When the others told him "We saw Jesus alive!" he said, "I won't believe unless I see and touch the nail marks myself."

A week later Jesus came back just for Thomas!

"Touch My hands and My side," Jesus said gently. "Stop doubting and believe."

Thomas looked at Jesus with eyes full of wonder and said, "My Lord and my God!"

Jesus smiled and said, "Blessed are those who believe without seeing."`,
  },
];

export default function EasterStory() {
  const [expanded, setExpanded] = useState(null);

  const toggleStage = (id) => {
    setExpanded(expanded === id ? null : id);
  };

  return (
    <div className="easter-root">
      <link rel="stylesheet" href={FONT_URL} />
      <style>{css}</style>
      <div className="easter-inner">

        {/* HERO */}
        <section className="text-center py-10">
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-md px-6 py-2 rounded-full border border-yellow-300 mb-6 shadow-sm">
            <Sparkles className="text-yellow-500" size={18} />
            <span className="font-semibold tracking-widest text-pink-600 text-sm">THE MOST WONDERFUL STORY</span>
          </div>

          <h1 className="serif text-5xl leading-none text-[#1E2937] mb-4">
            Jesus is Alive!
          </h1>
          <p className="text-2xl comic text-pink-600 mb-8">The Happy Easter Story</p>

          <div className="bg-white rounded-3xl p-8 shadow-xl border border-yellow-200 mb-10">
            <p className="text-lg leading-relaxed text-slate-700">
              Early on Sunday morning something amazing happened!<br />
              Jesus had died on the cross, but God made Him alive again!
            </p>
            <p className="mt-6 text-xl font-medium text-pink-600">
              He is alive forever — and He loves you so much! 💛
            </p>
          </div>
        </section>

        {/* STAGES */}
        <section>
          <div className="mb-8 text-center">
            <h2 className="serif text-3xl text-slate-800">What Happened on Easter?</h2>
            <p className="text-slate-600 mt-2">Tap each part to read and see the beautiful pictures</p>
          </div>

          <div className="space-y-4">
            {stages.map((stage) => {
              const isOpen = expanded === stage.id;
              const { Icon } = stage;

              return (
                <div key={stage.id} className="bg-white rounded-3xl overflow-hidden shadow-lg border border-yellow-100">
                  <button
                    onClick={() => toggleStage(stage.id)}
                    className="w-full px-6 py-5 flex items-center gap-4 text-left hover:bg-yellow-50 transition-colors"
                  >
                    <div 
                      className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${stage.accent}20`, color: stage.accent }}
                    >
                      <Icon size={28} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="text-pink-500 font-mono text-xs tracking-widest mb-1">
                        {stage.num}
                      </div>
                      <div className="text-xl font-semibold text-slate-800 leading-tight">
                        {stage.label}
                      </div>
                      <div className="text-sm text-slate-500 mt-1">{stage.sub}</div>
                    </div>

                    {isOpen ? (
                      <ChevronUp size={24} className="text-pink-500" />
                    ) : (
                      <ChevronDown size={24} className="text-slate-400" />
                    )}
                  </button>

                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-8 pt-2">
                          <div className="prose prose-slate text-[17px] leading-relaxed">
                            {stage.summary.split('\n\n').map((paragraph, i) => (
                              <p key={i} className="mb-4">{paragraph}</p>
                            ))}
                          </div>

                          {/* Illustration */}
                          <div className="mt-8 bg-gradient-to-br from-yellow-50 to-pink-50 rounded-2xl p-6 border border-yellow-200">
                            <img
                              src={stage.image}
                              alt={stage.label}
                              className="w-full rounded-xl shadow-md"
                            />
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </section>

        {/* CLOSING */}
        <div className="text-center mt-16 mb-12">
          <div className="text-6xl mb-6">🌷✝️🌷</div>
          <h3 className="serif text-3xl text-slate-800 mb-4">Jesus is alive today!</h3>
          <p className="max-w-xs mx-auto text-lg text-slate-600 leading-relaxed">
            He loves you very much and wants to be your best friend forever.<br />
            <span className="font-medium text-pink-600">Happy Easter!</span>
          </p>
        </div>
      </div>
    </div>
  );
}
